import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, defer, firstValueFrom, from, map, of, switchMap, tap, throwError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BrandColorOption } from '../_data/brand-color-options';
import { BusinessSetupProgressService } from './business-setup-progress.service';
import { BusinessSetupService, OnlineStoreCreatedPayload } from './business-setup.service';
import { ShippingChoice, buildShippingPayload } from '../utils/welcome-shipping';
import { DEFAULT_PRODUCT_CATEGORY, SAMPLE_PRODUCT_QUANTITY, buildFirstProductModel, sampleProductPrice, sampleProductsFor } from '../utils/welcome-product';
import { AppBaseDataService } from './app-base-data.service';
import { CategoryService } from './category.service';
import { ProductsService } from './products.service';
import { GlobalService } from './global.service';
import { UserService } from './user.service';

/** Every answer the live address check can give; the server is the authority. */
export type AddressCheck =
  | { status: 'available' }
  | { status: 'taken' }
  | { status: 'reserved' }
  | { status: 'invalid' }
  | { status: 'short' }
  | { status: 'error' };

/** What the address field shows: nothing typed yet, a check in flight, or the server's answer. */
export type AddressState = { status: 'idle' } | { status: 'checking' } | AddressCheck;

/** A template as the welcome page shows it. */
export interface WelcomeTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  /** Small card image; also the fallback for the two full-page images. */
  thumbnail: string;
  /** Full home page at desktop width (previewImages[0]), or the thumbnail. */
  desktopImage: string;
  /** Full home page at phone width (previewImages[1]), or the desktop image. */
  mobileImage: string;
  demoUrl: string;
  primaryColor: string;
}

export interface WelcomeCreateInput {
  /** The platform subdomain, e.g. "papajohn". */
  subdomain: string;
  template: WelcomeTemplate;
  /** The ONE optional brand colour. */
  brandColor?: BrandColorOption | null;
}

export type WelcomeCreateResult =
  | { ok: true; siteUrl: string }
  | { ok: false; code: string; message: string };

/** The fields of the business record the optional questions can change. */
export interface BusinessInfoPatch {
  businessDisplayName?: string;
  /** The business type; saved as "<type> || <description>". */
  businessType?: string;
  description?: string;
  /** Street address line 1. */
  street?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  preferredShippingProvider?: string;
  /** The business logo as a data URL; the business-info save uploads it and keeps the link. */
  logo?: string;
}

export const FALLBACK_TEMPLATE_IMAGE = 'assets/images/store-templates/tpl-classic.svg';
const SEPARATOR = ' || ';
/** sessionStorage flag: the sample products were added in this browser session, so they are not added twice. */
const SAMPLE_PRODUCTS_FLAG = 'welcome-sample-products-added';
/** How long saves wait for the account refresh after the store is created before going ahead without it. */
const REFRESH_TIMEOUT_MS = 10000;

/** Reads a business-setup field whichever casing the API used. */
function field(setup: any, name: string): any {
  if (!setup) return undefined;
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return setup[name] ?? setup[pascal];
}

/** "Fashion & Clothing || We sell…" → type and description. */
export function splitIndustry(industry: string | null | undefined): { type: string; description: string } {
  const text = String(industry || '');
  const at = text.indexOf(SEPARATOR);
  if (at < 0) return { type: text.trim(), description: '' };
  return { type: text.slice(0, at).trim(), description: text.slice(at + SEPARATOR.length).trim() };
}

/** "12 Main St || Suite 4" → street and second line. */
export function splitAddress(address: string | null | undefined): { street: string; address2: string } {
  const text = String(address || '');
  const at = text.indexOf(SEPARATOR);
  if (at < 0) return { street: text.trim(), address2: '' };
  return { street: text.slice(0, at).trim(), address2: text.slice(at + SEPARATOR.length).trim() };
}

/**
 * The whole business-info object EditBusinessInfoV2 expects: every field echoed from what the
 * account has, with `patch` applied on top — the endpoint blanks anything that is left out.
 * Same keys and encodings as the business-setup checklist.
 */
export function buildBusinessInfoPayload(setup: any, patch: BusinessInfoPatch = {}): any {
  const industry = splitIndustry(field(setup, 'industry'));
  const address = splitAddress(field(setup, 'address'));
  const has = (key: keyof BusinessInfoPatch) => patch[key] !== undefined;

  const type = has('businessType') ? String(patch.businessType) : industry.type;
  const description = has('description') ? String(patch.description) : (field(setup, 'description') || industry.description || '');
  const street = has('street') ? String(patch.street) : address.street;
  const address2 = has('address2') ? String(patch.address2) : (field(setup, 'address2') || address.address2 || '');
  const logo = patch.logo !== undefined ? String(patch.logo) : (field(setup, 'logo') ?? field(setup, 'businessLogo') ?? '');

  return {
    businessDisplayName: has('businessDisplayName')
      ? String(patch.businessDisplayName)
      : (field(setup, 'businessDisplayName') || field(setup, 'businessName') || ''),
    industry: type && description ? `${type}${SEPARATOR}${description}` : type,
    description,
    address: address2 ? `${street}${SEPARATOR}${address2}` : street,
    address2,
    city: has('city') ? String(patch.city) : (field(setup, 'city') || ''),
    state: has('state') ? String(patch.state) : (field(setup, 'state') || ''),
    zip: has('zip') ? String(patch.zip) : (field(setup, 'zip') || ''),
    country: has('country') ? String(patch.country) : (field(setup, 'country') || 'US'),
    phone: field(setup, 'phone') ?? '',
    logo,
    businessLogo: logo,
    preferredShippingProvider: has('preferredShippingProvider')
      ? String(patch.preferredShippingProvider)
      : (field(setup, 'preferredShippingProvider') || ''),
    businessId: field(setup, 'businessId')
  };
}

/** Maps one catalog entry (GET online-store/templates) to what the welcome page shows. */
export function mapWelcomeTemplate(raw: any): WelcomeTemplate {
  const encode = (url: unknown) => (url ? encodeURI(String(url)) : '');
  const previews: string[] = Array.isArray(raw?.previewImages) ? raw.previewImages.map(String).filter(Boolean) : [];
  const thumbnail = encode(raw?.thumbnailUrl) || encode(previews[0]) || FALLBACK_TEMPLATE_IMAGE;
  const desktopImage = encode(previews[0]) || thumbnail;
  return {
    id: String(raw?.id ?? ''),
    name: String(raw?.label ?? raw?.id ?? ''),
    description: String(raw?.description ?? ''),
    category: String(raw?.category ?? ''),
    tags: Array.isArray(raw?.tags) ? raw.tags.map(String) : [],
    thumbnail,
    desktopImage,
    mobileImage: encode(previews[1]) || desktopImage,
    demoUrl: String(raw?.demoUrl ?? ''),
    primaryColor: String(raw?.primaryColor ?? '')
  };
}

/** Which address-check answer a response body (success or 400) stands for. */
export function mapAddressCheck(body: any, httpOk: boolean): AddressCheck {
  const code = body?.code ?? body?.error?.code;
  if (code === 'SUBDOMAIN_RESERVED') return { status: 'reserved' };
  if (code === 'SUBDOMAIN_TOO_SHORT') return { status: 'short' };
  if (code === 'SUBDOMAIN_INVALID') return { status: 'invalid' };
  if (httpOk && body?.success === true) {
    return (body.data?.available ?? body.available) ? { status: 'available' } : { status: 'taken' };
  }
  return { status: 'error' };
}

/** What the merchant has entered on the welcome page so far, kept while they are away from it. */
export interface WelcomeDraft {
  step: 1 | 2 | 3;
  storeName: string;
  subdomain: string;
  addressTouched: boolean;
  businessType: string | null;
  about: string;
  logo: string;
  brandColor: BrandColorOption | null;
  selectedId: string | null;
  autoPicked: boolean;
}

/** Orchestrates the welcome-store page over the dashboard's existing calls. */
@Injectable({ providedIn: 'root' })
export class WelcomeStoreService {
  /**
   * The welcome page was opened in this tab. The signup pages then send the merchant back to it instead of
   * starting over (browser Back after signup). In memory: the full sign-in clears the browser's storage.
   */
  welcomeOpened = false;
  /** The page's answers, for the business they belong to; in memory for the same reason. */
  private draft: { businessId: string; value: WelcomeDraft } | null = null;

  constructor(
    private http: HttpClient,
    private businessSetupService: BusinessSetupService,
    private userService: UserService,
    private progress: BusinessSetupProgressService,
    private productsService: ProductsService,
    private categoryService: CategoryService,
    private appBaseData: AppBaseDataService
  ) {}

  /** The account's business record (what the page prefills from and writes back to). */
  get businessSetup(): any {
    return GlobalService.userInfo?.businessSetup?.[0] ?? null;
  }

  /** Present when the account already has a store; the page then shows it instead of the flow. */
  get existingStoreUrl(): string {
    const setup = this.businessSetup;
    return setup && field(setup, 'onlineStoreCreated') ? String(field(setup, 'onlineStoreUrl') || '') : '';
  }

  private get businessKey(): string {
    const setup = this.businessSetup;
    return String(field(setup, 'businessId') ?? field(setup, 'id') ?? '');
  }

  saveDraft(value: WelcomeDraft): void {
    this.draft = { businessId: this.businessKey, value };
  }

  /** The answers left on the page earlier, unless they belong to another business. */
  takeDraft(): WelcomeDraft | null {
    const draft = this.draft;
    this.draft = null;
    return draft && draft.businessId === this.businessKey ? draft.value : null;
  }

  clearDraft(): void {
    this.draft = null;
  }

  private get username(): string {
    return String(GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? '');
  }

  /**
   * GET online-store/check-domain. Uses HttpClient directly because BaseService turns every HTTP error into
   * `{success:false}` and loses the named problems (SUBDOMAIN_TAKEN/RESERVED/…) a 400 carries.
   */
  checkAddress(subdomain: string): Observable<AddressCheck> {
    const headers = new HttpHeaders({ 'do-need-to-process-interceptor': 'skip' });
    return this.http.get<any>(environment.AuthenticationURL + 'online-store/check-domain', { params: { domain: subdomain }, headers }).pipe(
      map((body) => mapAddressCheck(body, true)),
      catchError((error: HttpErrorResponse) => of(mapAddressCheck(error?.error, false)))
    );
  }

  /** The published templates, mapped for the page. An empty list on any failure. */
  loadTemplates(): Observable<WelcomeTemplate[]> {
    const businessIdentifier = String(field(this.businessSetup, 'businessNameWithDomain') ?? '');
    if (!this.username || !businessIdentifier) return of([]);
    return this.businessSetupService.getOnlineStoreTemplates(this.username, businessIdentifier).pipe(
      map((res: any) => {
        const templates = res?.data?.templates;
        return res?.success === true && Array.isArray(templates) ? templates.map(mapWelcomeTemplate) : [];
      }),
      catchError(() => of([] as WelcomeTemplate[]))
    );
  }

  /**
   * Saves the business name and type (and any optional answer) on the account, echoing every other field.
   * Saves run one after another: each one builds its payload from the record as the previous one left it,
   * so a quick Next in the optional questions can never overwrite the name/type save still in flight.
   */
  saveBusinessInfo(patch: BusinessInfoPatch): Observable<boolean> {
    return defer(() => {
      const run = this.saveQueue.then(() => firstValueFrom(this.saveNow(patch)));
      this.saveQueue = run.catch(() => false);
      return from(run);
    });
  }

  private saveQueue: Promise<unknown> = Promise.resolve();

  private saveNow(patch: BusinessInfoPatch): Observable<boolean> {
    const setup = this.businessSetup;
    if (!setup) return of(false);
    const payload = buildBusinessInfoPayload(setup, patch);
    return this.businessSetupService.saveBusinessInformationV2(payload).pipe(
      map((res: any) => {
        if (res?.success !== true) return false;
        this.patchLocalBusinessSetup(payload);
        this.progress.invalidate();
        // Refresh locations and filters in the background (the checklist did this after a save); never affects the save.
        this.appBaseData.fetchAllFilters(false).pipe(catchError(() => of(null))).subscribe();
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Saves the shipping zone for the chosen rate type, the same payload Settings > Shipping sends. The first
   * zone already on the account (if any) is updated in place; otherwise one is created. Saving completes the
   * checklist's Shipping step (BusinessSetupService.shippingSaved$).
   */
  applyShippingPreference(choice: ShippingChoice, flatCost?: number): Observable<boolean> {
    const setup = this.businessSetup;
    if (!setup) return of(false);
    return this.businessSetupService.getShippingConfiguration(true).pipe(
      map((res: any) => {
        const data = res?.data;
        return (Array.isArray(data) ? data : data?.zones) || [];
      }),
      catchError(() => of([] as any[])),
      switchMap((existingZones: any[]) => {
        const payload = buildShippingPayload({ choice, flatCost, setup, existingZones });
        const save$ = existingZones.length > 0
          ? this.businessSetupService.updateShippingConfiguration(null, payload)
          : this.businessSetupService.saveShippingConfiguration(payload);
        return save$.pipe(map((res: any) => res?.success === true));
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Adds the merchant's first product with the same Items v3 call the setup checklist uses, in a category named
   * after the business type (created if it does not exist yet).
   */
  addFirstProduct(input: { name: string; price: number; quantity: number; image?: string; categoryName?: string }): Observable<boolean> {
    const setup = this.businessSetup;
    if (!setup) return of(false);
    const categoryName = (input.categoryName || DEFAULT_PRODUCT_CATEGORY).trim() || DEFAULT_PRODUCT_CATEGORY;
    return from(this.resolveCategoryId(categoryName, setup)).pipe(
      switchMap((categoryId) => {
        const model = buildFirstProductModel({
          name: input.name.trim(),
          price: input.price,
          quantity: input.quantity,
          image: input.image,
          categoryId,
          setup,
          userId: GlobalService.userInfo?.userID,
          userLocations: GlobalService.userInfo?.userLocations,
          sku: GlobalService.generateSKU?.() || `SKU-${Date.now()}`,
          barcode: `${Math.floor(100000000000 + Math.random() * 900000000000)}`
        });
        return this.productsService.saveProductV3(model);
      }),
      map((res: any) => {
        if (res?.success !== true) return false;
        this.productsService.notifyFirstProductCreated(true);
        this.progress.invalidate();
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Adds the four sample products for a business type, one after another, in a category named after the type.
   * `prices` lines up with `sampleProductsFor(typeId)` (the screen shows them before they are saved). A product that
   * fails to save does not stop the rest. Returns how many were saved; once any was, a second call adds nothing, so a
   * refresh cannot duplicate them.
   */
  addSampleProducts(businessTypeId: number, categoryName: string, prices: number[]): Observable<number> {
    const setup = this.businessSetup;
    const samples = sampleProductsFor(businessTypeId);
    if (!setup || samples.length === 0 || this.sampleProductsAdded()) return of(0);
    const category = (categoryName || DEFAULT_PRODUCT_CATEGORY).trim() || DEFAULT_PRODUCT_CATEGORY;
    return from(this.resolveCategoryId(category, setup)).pipe(
      switchMap((categoryId) => from(this.saveSamples(samples, prices, categoryId, setup))),
      map((saved) => {
        if (saved > 0) {
          try { sessionStorage.setItem(SAMPLE_PRODUCTS_FLAG, '1'); } catch { /* the guard is best-effort */ }
          this.productsService.notifyFirstProductCreated(true);
          this.progress.invalidate();
        }
        return saved;
      }),
      catchError(() => of(0))
    );
  }

  private sampleProductsAdded(): boolean {
    try { return sessionStorage.getItem(SAMPLE_PRODUCTS_FLAG) === '1'; } catch { return false; }
  }

  private async saveSamples(samples: ReturnType<typeof sampleProductsFor>, prices: number[], categoryId: any, setup: any): Promise<number> {
    let saved = 0;
    const usedSkus = new Set<string>();
    for (let i = 0; i < samples.length; i++) {
      let sku = GlobalService.generateSKU?.() || `SKU-${Date.now()}-${i}`;
      for (let attempt = 0; usedSkus.has(sku) && attempt < 5; attempt++) sku = GlobalService.generateSKU?.() || `SKU-${Date.now()}-${i}-${attempt}`;
      usedSkus.add(sku);
      const price = prices[i] ?? sampleProductPrice();
      const model = buildFirstProductModel({
        name: samples[i].name,
        price,
        quantity: SAMPLE_PRODUCT_QUANTITY,
        image: samples[i].imageUrl,
        categoryId,
        setup,
        userId: GlobalService.userInfo?.userID,
        userLocations: GlobalService.userInfo?.userLocations,
        sku,
        barcode: `${Math.floor(100000000000 + Math.random() * 900000000000)}`
      });
      model.description = samples[i].description;
      try {
        const res: any = await firstValueFrom(this.productsService.saveProductV3(model));
        if (res?.success === true) saved++;
      } catch { /* one failed product does not stop the others */ }
    }
    return saved;
  }

  private findCategoryId(name: string): any {
    const found = (this.appBaseData.categories || []).find((c: any) => String(c.categoryName || '').toLowerCase() === name.toLowerCase());
    return found?.id;
  }

  /** The id of the named category, creating the category first when it does not exist (same steps as the checklist). */
  private async resolveCategoryId(name: string, setup: any): Promise<any> {
    const existing = this.findCategoryId(name);
    if (existing) return existing;
    const created: any = await firstValueFrom(this.categoryService.createUpdateCategory({
      id: null,
      categoryName: name,
      departmentId: setup?.departmentId || 1,
      isActive: true,
      addedBy: GlobalService.userInfo?.userID,
      isDefault: false,
      partitionKey: null,
      createdDate: new Date(),
      lastEdited: new Date(),
      image: ''
    }));
    if (created?.success !== true) throw new Error('Could not create the category.');
    this.appBaseData.getAllCategories();
    // The new id appears once the category list reloads: wait for it, a few times, like the checklist does.
    for (let attempt = 0; attempt < 8; attempt++) {
      const id = this.findCategoryId(name);
      if (id) return id;
      await new Promise((resolve) => setTimeout(resolve, 300 * Math.pow(1.5, attempt)));
    }
    throw new Error('Category created but its id never appeared.');
  }

  private patchLocalBusinessSetup(payload: any): void {
    const setup = this.businessSetup;
    if (!setup) return;
    ['businessDisplayName', 'industry', 'description', 'address', 'address2', 'city', 'state', 'zip', 'country', 'preferredShippingProvider']
      .forEach((key) => { setup[key] = payload[key]; });
  }

  /**
   * Creates the store through the existing gateway, then records it on the account.
   * One brand colour at most: its name is the whole `brandColors` list, its hex the template's primary colour.
   */
  createStore(input: WelcomeCreateInput): Observable<WelcomeCreateResult> {
    const setup = this.businessSetup;
    const userId = String(GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? '');
    const businessId = field(setup, 'businessId') ?? field(setup, 'id');
    if (!setup || !userId || businessId === undefined || !this.username) {
      return of({ ok: false, code: 'NO_ACCOUNT', message: 'We could not load your account. Please refresh and try again.' });
    }

    const { subdomain, template, brandColor } = input;
    const brandColors = brandColor ? [brandColor.name] : [];
    const primaryColor = brandColor?.hex || template.primaryColor || undefined;

    return this.businessSetupService.createB2CSite(userId, businessId, this.username, subdomain, template.id, brandColors, {
      templateLabel: template.name,
      templateThumbnailUrl: template.thumbnail,
      templateCategory: template.category,
      templateDemoUrl: template.demoUrl,
      templatePrimaryColor: primaryColor,
      templatePreviewImages: [template.desktopImage, template.mobileImage].filter(Boolean)
    }).pipe(
      switchMap((editorResponse: any) => {
        const siteUrl = String(
          editorResponse?.onlineStoreUrl ?? editorResponse?.url ?? editorResponse?.data?.onlineStoreUrl ?? editorResponse?.data?.url ?? ''
        ).trim();
        if (editorResponse?.success !== true || !siteUrl) {
          return throwError(() => new Error(editorResponse?.message || ''));
        }
        const payload: OnlineStoreCreatedPayload = {
          userId,
          businessId,
          onlineStoreUrl: siteUrl,
          onlineStoreName: subdomain.charAt(0).toUpperCase() + subdomain.slice(1),
          onlineStoreTemplateId: template.id,
          onlineStoreTemplateLabel: template.name,
          onlineStoreTemplateThumbnailUrl: template.thumbnail,
          onlineStoreTemplateCategory: template.category,
          onlineStoreTemplateDemoUrl: template.demoUrl,
          onlineStoreTemplatePrimaryColor: primaryColor,
          onlineStoreTemplatePreviewImages: [template.desktopImage, template.mobileImage].filter(Boolean),
          brandColors
        };
        const siteId = editorResponse?.data?.siteId ?? editorResponse?.data?.id;
        if (siteId) payload.webStoreId = siteId;
        return this.businessSetupService.markOnlineStoreCreated(payload).pipe(
          map((completion: any) => {
            if (completion?.success !== true) throw new Error(completion?.message || '');
            return { siteUrl, completed: completion?.data };
          })
        );
      }),
      map(({ siteUrl, completed }): WelcomeCreateResult => {
        this.afterCreated(siteUrl, subdomain, template, brandColors, completed);
        return { ok: true, siteUrl };
      }),
      catchError((error: any) => {
        const message = String(error?.error?.message || error?.message || '');
        // The gateway only passes the editor's message through; the named code is lost on the way.
        const code = /taken|already (in use|exists)|not available/i.test(message) ? 'SUBDOMAIN_TAKEN' : 'CREATE_FAILED';
        return of<WelcomeCreateResult>({
          ok: false,
          code,
          message: message || 'We could not create your store. Please try again.'
        });
      })
    );
  }

  /** Patches the cached account the way the existing store setup does, then refetches the saved record. */
  private afterCreated(siteUrl: string, subdomain: string, template: WelcomeTemplate, brandColors: string[], completed: any): void {
    const setup = this.businessSetup;
    if (setup) {
      setup.onlineStoreUrl = completed?.onlineStoreUrl ?? siteUrl;
      setup.onlineStoreCreated = completed?.onlineStoreCreated ?? true;
      setup.onlineStoreCreatedDate = completed?.onlineStoreCreatedDate ?? setup.onlineStoreCreatedDate ?? new Date().toISOString();
      setup.onlineStoreName = completed?.onlineStoreName ?? (subdomain.charAt(0).toUpperCase() + subdomain.slice(1));
      setup.onlineStoreTemplateId = completed?.onlineStoreTemplateId ?? template.id;
      setup.brandColorSelections = brandColors.slice();
    }
    const email = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;
    if (email) {
      // The refreshed account replaces the cached one, which every business-info save echoes back. Run it on the save
      // queue, so a save waits for it instead of being echoed from a copy that this refresh then replaces with an older one.
      const refresh$ = this.userService.getUserByUserName(email).pipe(
        timeout(REFRESH_TIMEOUT_MS),
        tap((userRes: any) => {
          if (userRes?.success && userRes?.data) GlobalService.userInfo = userRes.data;
        }),
        catchError(() => of(null))
      );
      this.saveQueue = this.saveQueue.then(() => firstValueFrom(refresh$, { defaultValue: null })).catch(() => null);
    }
    // The dashboard's store link / tile read this; the old store-creation flows refreshed it too.
    this.userService.getWebStoreConfiguration(true).pipe(catchError(() => of(null))).subscribe((res: any) => {
      if (res?.success) GlobalService.webStoreConfiguration = res.data;
    });
    this.progress.markDomainTemplateSaved();
  }

  /**
   * "Need something custom? Contact us": the same custom-online-store request the setup checklist used to send.
   * Marks the store step as requested (so the checklist shows it as pending) and refreshes the account.
   */
  requestCustomStore(input: { subdomain: string; template?: WelcomeTemplate | null; brandColor?: BrandColorOption | null }): Observable<boolean> {
    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId;
    const userEmail = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;
    const businessId = field(this.businessSetup, 'businessId') ?? field(this.businessSetup, 'id');
    if (!userId || !userEmail || businessId === undefined) return of(false);

    return this.businessSetupService.requestCustomOnlineStore({
      userId,
      businessId,
      userEmail,
      subdomain: input.subdomain,
      brandColors: input.brandColor ? [input.brandColor.name] : [],
      templateName: input.template?.name,
      templateImageUrl: input.template?.thumbnail
    }).pipe(
      switchMap((res: any) => {
        if (res?.success === false) return of(false);
        return this.userService.getUserByUserName(userEmail).pipe(
          catchError(() => of(null)),
          map((userRes: any) => {
            if (userRes?.success && userRes?.data) GlobalService.userInfo = userRes.data;
            const setup = this.businessSetup;
            if (setup) {
              setup.customOnlineStoreRequested = true;
              setup.CustomOnlineStoreRequested = true;
            }
            this.progress.markCustomOnlineStoreRequested();
            return true;
          })
        );
      }),
      catchError(() => of(false))
    );
  }

  /** Hands an already-opened tab to the editor with a one-time code (popup-safe: the caller opens the tab on click). */
  openEditor(editorTab: Window | null): Observable<boolean> {
    const businessIdentifier = String(field(this.businessSetup, 'businessNameWithDomain') ?? '');
    return this.businessSetupService.getFreshStoreEditorAuthCode(this.username, businessIdentifier).pipe(
      map((res: any) => {
        const code = res?.data?.code;
        if (res?.success === false || !code) {
          editorTab?.close();
          return false;
        }
        const target = `${environment.StoreEditorBaseURL}auth/callback?code=${encodeURIComponent(code)}`;
        if (editorTab) editorTab.location.href = target;
        else window.open(target, '_blank');
        return true;
      }),
      catchError(() => {
        editorTab?.close();
        return of(false);
      })
    );
  }
}
